/**
 * Reads coordinates out of a pasted Google Maps link.
 *
 * The API has the same logic in App\Rules-adjacent MapLink, but OUP-5 made
 * latitude and longitude *required*, and validation runs before the controller
 * — so the server can no longer reach its own "both blank, derive them from the
 * link" branch. A blank pair is a 422 now, not an invitation.
 *
 * That leaves the work here, one step earlier, where it still helps: paste a
 * Maps link, and the two coordinate boxes fill themselves, so the required
 * fields are satisfied by the thing the user already had in their clipboard.
 *
 * Same forms the PHP reads, in the same order of trust:
 *   1. !3d24.7136!4d46.6753  — the place pin, the branch's actual location
 *   2. @24.7136,46.6753,17z  — the viewport centre; close, but the camera
 *   3. ?q= / ?query= / ?ll= / ?destination=  — the explicit query forms
 *
 * A short link (maps.app.goo.gl/xyz) carries no coordinates and yields null.
 * Following the redirect would mean a cross-origin request from the browser to
 * resolve someone's address, which is not a trade worth making.
 */

const NUMBER = String.raw`-?\d{1,3}(?:\.\d+)?`;

const PATTERNS = [
  new RegExp(String.raw`!3d(${NUMBER})!4d(${NUMBER})`),
  new RegExp(String.raw`@(${NUMBER}),(${NUMBER})`),
  new RegExp(String.raw`[?&](?:q|query|ll|destination)=(${NUMBER})%2C\s*(${NUMBER})`, "i"),
  new RegExp(String.raw`[?&](?:q|query|ll|destination)=(${NUMBER}),\s*(${NUMBER})`, "i"),
];

/**
 * @param {string|null|undefined} url
 * @returns {{latitude: string, longitude: string}|null}
 */
export const coordinatesFrom = (url) => {
  if (typeof url !== "string" || url.trim() === "") return null;

  for (const pattern of PATTERNS) {
    const found = pattern.exec(url);
    if (!found) continue;

    const latitude = Number(found[1]);
    const longitude = Number(found[2]);

    // A match that is not a coordinate matched something else — a zoom level,
    // an id, part of a place name. Drop it rather than filling a field the
    // branch will later be searched by.
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) continue;

    return { latitude: found[1], longitude: found[2] };
  }

  return null;
};
